/**
 * Everything the local user does inside a voice room.
 *
 * Each action patches the store optimistically, drives the media client, and
 * then tells the server — in that order, because the person who pressed mute
 * must see the icon change immediately and the room must hear it before the
 * REST call completes.
 *
 * ── THE ONE RULE EVERY GUARD IN THIS FILE OBEYS ───────────────────────────
 *
 *   AN EARLY RETURN MAY DECLINE TO OPEN A MICROPHONE.
 *   AN EARLY RETURN MAY NEVER DECLINE TO CLOSE ONE.
 *
 * It is written here because breaking it produced a microphone that NOTHING in
 * the product could switch off — not push-to-talk, not the in-app key, not
 * blur, not a screen lock, not navigating away:
 *
 *   `setMicEnabled` computed `transmitting = on && inputMode !== 'push-to-talk'`
 *   and never consulted `deafened`, so unmuting while deafened — three clicks,
 *   no timing, no desktop shell — unmuted the published track. `setTransmitting`
 *   is the single door every close in the product goes through, and its guard
 *   read `if (client === null || !micEnabled || deafened) return;`. The same
 *   `deafened` that let the microphone open then refused every attempt to shut
 *   it. `reportState()` told the server `isMuted: false` while the user's own
 *   UI said they were deafened, so the roster showed them unmuted and the room
 *   heard them.
 *
 * Two lines, each defensible alone, composing into a state with no exit. The
 * repair is not a third condition — it is the shape: the CLOSE path has no
 * guard on it at all beyond "is there a client", and the facts that may refuse
 * (`micEnabled`, `deafened`, "already open") sit exclusively on the OPEN path.
 *
 * FOUR EARLY RETURNS DECLINE AN OPEN, and all four are recoverable by the next
 * press or the next sample:
 *   • no client        — there is no room, so there is nothing to transmit into
 *   • the mic is muted — capture is stopped; unmute first
 *   • deafened         — deafen promises "silence everyone AND stop
 *                        transmitting", and a deafened user who is publishing
 *                        is that promise broken
 *   • already open     — idempotence. Voice activity asks 20 times a second and
 *                        each ask would otherwise be a `unmute()` + roster sync.
 *
 * THE DESKTOP SHELL IS NOT TOLD ABOUT ANY OF THEM. It briefly was:
 * `setTransmitting` answered with the truth, the renderer sent that back over
 * IPC, and the shell dropped gates the renderer had refused. The answer could
 * not be dated against the request it belonged to, so a late one dropped a gate
 * a later press had legitimately opened. That layer is gone and the global
 * push-to-talk gate it guarded is gone with it — apps/desktop/src/main/hotkeys.ts
 * records why. What still drives this file is the IN-APP key (hooks/useVoiceInput.ts),
 * which gets a real DOM `keyup`, and voice activity, which owns a timer.
 */
import type { ScreenShareHint, ShareStartResult } from '@/features/meeting/media/contract';
import { voiceApi } from '../api/voiceApi';
import { patchSession, voiceSessionStore } from '../store/voiceSessionStore';
import { voicePrefs } from '../store/voicePrefs';
import { applyLocalGains } from './voiceGains';
import { applyVoicePlan } from './voicePlan';

/**
 * A start that is already in flight, so a second press joins it rather than
 * opening a second capture. See `voiceControls.startScreenShare`.
 */
let startingShare: Promise<ShareStartResult> | null = null;

const reportState = (): void => {
  const state = voiceSessionStore.get();
  if (state.channelId === null) return;
  void voiceApi
    .setState(state.channelId, {
      isMuted: !state.micEnabled || !state.transmitting,
      isDeafened: state.deafened,
    })
    .catch(() => undefined);
};

/**
 * MUTE THE PUBLISHED TRACK. The only close path, and it cannot refuse.
 *
 * No `micEnabled` check and no `deafened` check: both of those are reasons the
 * microphone should not be OPEN, which makes them reasons to carry this out,
 * never to skip it. No "it is already closed" check either — `track.mute()` on
 * a muted track is a no-op inside LiveKit, and the store is the thing that was
 * wrong the one time this mattered, so the store is not allowed to veto it.
 */
async function closeTransmission(): Promise<void> {
  const state = voiceSessionStore.get();
  if (state.transmitting) patchSession({ transmitting: false });
  /* No client means no published track, so there is no microphone to close.
     That is "nothing to do", not "refuse to do it" — the store has already been
     corrected above, so the UI cannot be left claiming a live microphone. */
  if (state.client === null) return;
  await state.client.setMicTransmitting(false);
}

export const voiceControls = {
  /**
   * The big mute button. Stops CAPTURE, so the OS microphone indicator goes
   * dark — a listener who muted for ten minutes should be able to see that the
   * app is not holding their microphone open.
   *
   * `deafened` IS CONSULTED HERE. Unmuting is an open, and the open rules apply
   * to it: turning capture back on while deafened restores capture and leaves
   * the published track muted, which is what the deafen glyph the user is
   * looking at already claims.
   */
  async setMicEnabled(on: boolean): Promise<void> {
    const state = voiceSessionStore.get();
    if (!on) {
      /* MUTING IS A CLOSE, so it is split out and it has no guard in front of
         it. The store is corrected first and unconditionally, and a missing
         client only means there is no capture to stop - never a reason to leave
         the UI claiming a live microphone. */
      patchSession({ micEnabled: false, transmitting: false });
      if (state.client !== null) await state.client.setMicEnabled(false);
      reportState();
      return;
    }
    if (state.client === null) return;
    const transmitting = !state.deafened && voicePrefs.get().inputMode !== 'push-to-talk';
    patchSession({ micEnabled: true, transmitting });
    await state.client.setMicEnabled(true);
    await state.client.setMicTransmitting(transmitting);
    reportState();
  },

  /**
   * The push-to-talk / voice-activity gate. Mutes the published track only, so
   * it is instant and repeatable at key-press rates.
   *
   * Read the header before adding a condition to this function. The close half
   * has no conditions on purpose.
   */
  async setTransmitting(on: boolean): Promise<void> {
    if (!on) {
      await closeTransmission();
      return;
    }
    const state = voiceSessionStore.get();
    if (state.client === null || !state.micEnabled || state.deafened) return;
    if (state.transmitting) return;
    patchSession({ transmitting: true });
    await state.client.setMicTransmitting(true);
  },

  /**
   * Deafen: silence everyone AND stop transmitting. Local-only — nobody is told
   * they have been tuned out beyond the mic glyph they can already see.
   *
   * DEAFENING CLOSES. UN-DEAFENING DOES NOT OPEN, and it does not touch the
   * published track at all. It used to compute `setMicTransmitting(!on && state.transmitting)`,
   * which is a call that CAN unmute, on a path with no key press and no voice
   * behind it. Re-opening is the input gate's job: voice activity re-opens on
   * the next sample above the sensitivity floor (~50 ms if you are talking) and
   * push-to-talk waits for a key, which is the only right answer for a user who
   * is not holding one.
   */
  async setDeafened(on: boolean): Promise<void> {
    const state = voiceSessionStore.get();
    if (state.client === null) return;
    if (on) {
      patchSession({ deafened: true, transmitting: false });
      applyLocalGains(state.client);
      await state.client.setMicTransmitting(false);
      reportState();
      return;
    }
    patchSession({ deafened: false });
    applyLocalGains(state.client);
    reportState();
  },

  /**
   * `channelId` is needed to TELL THE SERVER, not to switch the camera off, so
   * it no longer gates the call. It could be null with a live client for the
   * width of a join/leave await, and "you cannot turn your camera off right
   * now" is the same class of defect as a microphone that will not close.
   */
  async setCameraEnabled(on: boolean): Promise<void> {
    const { client, channelId } = voiceSessionStore.get();
    if (client === null) return;
    await client.setCameraEnabled(on);
    if (channelId === null) return;
    void voiceApi.setState(channelId, { isVideoEnabled: on }).catch(() => undefined);
  },

  /**
   * Share a screen INSIDE the voice room. `detail` is the right default for the
   * thing people actually share here — an editor or a terminal, where legible
   * text matters more than smooth motion.
   *
   * STARTING may decline (a share the server is never told about is worse than
   * no share); STOPPING may not — see `stopScreenShare`.
   *
   * A voice room holds one stage exactly as a meeting does (`VoiceStage` renders
   * a single "X is sharing a screen"), so it obeys the same one-presenter rule.
   * The RESULT is returned rather than swallowed: a start that was refused must
   * not report `isScreenSharing: true` to the server for a share that never
   * began, and the caller is the only thing that can offer the take-over.
   */
  async startScreenShare(
    hint: ScreenShareHint = 'detail',
    takeOver = false,
  ): Promise<ShareStartResult> {
    const { client, channelId } = voiceSessionStore.get();
    if (client === null || channelId === null) return { status: 'started', withAudio: false };
    /*
     * ONE START AT A TIME — the guard for the path with no picker in front of it.
     *
     * Every other Share button opens an in-app chooser first, which closes on
     * the press and cannot be pressed twice. This one calls straight through,
     * and `capturing` stays false until the capture resolves, so a second press
     * while the browser's own chooser is open is delivered and starts a SECOND
     * `getDisplayMedia`. On Chrome and Edge that cannot happen (their chooser is
     * modal) and the desktop shell cancels the first picker — Firefox's prompt
     * is a non-modal doorhanger, and it can.
     *
     * The second press is COALESCED rather than refused: the person asked to
     * share and gets the answer to that question, including the take-over offer
     * a refusal carries. A refusal would look like a dead button.
     */
    if (startingShare !== null) return startingShare;
    const attempt = client.startScreenShare(hint, takeOver).finally(() => {
      startingShare = null;
    });
    startingShare = attempt;
    const result = await attempt;
    /*
     * ONLY A SHARE THAT ACTUALLY STARTED IS REPORTED.
     *
     * `blocked` (somebody else holds the stage) and `denied` (a host revoked
     * this participant's share) are both shares that never began. Only
     * `blocked` was guarded, so `denied` — the status the newly wired host verb
     * produces — fell straight through this line and told the server this
     * client was sharing. `VoiceMemberList` and `VoiceOccupantChip` both read
     * that flag, so the room drew a phantom sharer, and nothing corrected it
     * until the next heartbeat 30 seconds later. That is the same symptom
     * one-presenter-at-a-time exists to end, so the test is the status, not the
     * one status that happened to exist when this was written.
     */
    if (result.status !== 'started') return result;
    void voiceApi.setState(channelId, { isScreenSharing: true }).catch(() => undefined);
    return result;
  },

  /**
   * THE SHARE ENDED WITHOUT THIS CLIENT ASKING — tell the server so.
   *
   * The provider stops a losing or host-stopped share underneath every room,
   * which is right, and it knows nothing about `voiceApi`. Until the next
   * heartbeat rewrote it (`voiceConnection.HEARTBEAT_MS`, 30 s) the channel
   * therefore went on advertising `isScreenSharing: true` for a share that no
   * longer existed — `VoiceMemberList` and `VoiceOccupantChip` both read it —
   * so for half a minute the room showed a phantom second sharer, which is the
   * exact symptom one-presenter-at-a-time exists to end.
   */
  reportShareStopped(): void {
    const { channelId } = voiceSessionStore.get();
    if (channelId === null) return;
    void voiceApi.setState(channelId, { isScreenSharing: false }).catch(() => undefined);
  },

  /** A capture nobody can stop is the same defect as a microphone nobody can
   *  close, so `channelId` gates the REPORT and not the stop. */
  async stopScreenShare(): Promise<void> {
    const { client, channelId } = voiceSessionStore.get();
    if (client === null) return;
    await client.stopScreenShare();
    if (channelId === null) return;
    void voiceApi.setState(channelId, { isScreenSharing: false }).catch(() => undefined);
  },

  /**
   * "Watch" — subscribe to the room's screen share. Until this is called the
   * share is not forwarded to this client at all, which is what keeps a chatty
   * room with one presenter cheap for everyone who is only listening.
   */
  setWatchingShare(on: boolean): void {
    const { client } = voiceSessionStore.get();
    patchSession({ watchingShare: on });
    if (client !== null) applyVoicePlan(client, on);
  },

  /** Per-listener volume. Persisted immediately; applied to the live room. */
  setUserVolume(userId: string, volume: number): void {
    voicePrefs.setVolume(userId, volume);
    const { client } = voiceSessionStore.get();
    if (client !== null) applyLocalGains(client);
  },

  toggleUserMuted(userId: string): void {
    voicePrefs.toggleMuted(userId);
    const { client } = voiceSessionStore.get();
    if (client !== null) applyLocalGains(client);
  },
} as const;
